/**
 * LyraPendingDocuments — Admin
 * Queue des documents reçus via N8N (Google Drive) en attente de validation.
 * Approuver → indexation dans la base de connaissance Lyra.
 * Rejeter → suppression.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { CloudDownload, Check, X, Loader, FileText, ExternalLink, RefreshCw } from 'lucide-react';
import { getLyraPendingDocuments, approveLyraPendingDocument, rejectLyraPendingDocument } from '../../api';

const STATUS_BADGE = {
  pending:  { label: 'En attente', cls: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Approuvé',   cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejeté',     cls: 'bg-red-100 text-red-700' },
};

const PendingRow = ({ doc, onApprove, onReject }) => {
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const handleApprove = async () => {
    if (!window.confirm(`Approuver et indexer "${doc.name}" dans la base Lyra ?`)) return;
    setApproving(true);
    try { await approveLyraPendingDocument(doc.id); onApprove(); } catch {}
    setApproving(false);
  };

  const handleReject = async () => {
    if (!window.confirm(`Rejeter et supprimer "${doc.name}" ?`)) return;
    setRejecting(true);
    try { await rejectLyraPendingDocument(doc.id); onReject(); } catch {}
    setRejecting(false);
  };

  const badge = STATUS_BADGE[doc.status] || STATUS_BADGE.pending;

  return (
    <div className="border border-gray-200 rounded-xl bg-white">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
          <FileText size={16} className="text-amber-500" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800 text-sm truncate">{doc.name}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
            <span>{doc.fileName}</span>
            {doc.fileSize && <span>{(doc.fileSize / 1024 / 1024).toFixed(2)} Mo</span>}
            {doc.source && <span>Source : {doc.source}</span>}
            <span>{new Date(doc.createdAt).toLocaleDateString('fr-FR')}</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {doc.fileUrl && (
            <a href={doc.fileUrl} target="_blank" rel="noreferrer"
              className="p-1.5 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition"
              title="Prévisualiser">
              <ExternalLink size={14} />
            </a>
          )}
          <button
            onClick={handleApprove}
            disabled={approving || rejecting}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg text-xs font-medium transition disabled:opacity-40"
            title="Approuver et indexer"
          >
            {approving ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
            Approuver
          </button>
          <button
            onClick={handleReject}
            disabled={approving || rejecting}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-xs font-medium transition disabled:opacity-40"
            title="Rejeter"
          >
            {rejecting ? <Loader size={12} className="animate-spin" /> : <X size={12} />}
            Rejeter
          </button>
        </div>
      </div>
    </div>
  );
};

const LyraPendingDocuments = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLyraPendingDocuments();
      setDocs(res.data || []);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <CloudDownload size={16} className="text-amber-500" />
              Validation Drive — Documents en attente
              {docs.length > 0 && (
                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                  {docs.length}
                </span>
              )}
            </CardTitle>
            <button onClick={fetchPending} disabled={loading}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 rounded-lg px-4 py-3 text-sm text-amber-800 border border-amber-100">
            Ces documents ont été envoyés automatiquement depuis Google Drive via N8N.
            <strong> Prévisualisez-les</strong> avant de décider s'ils doivent intégrer la base de connaissance de Lyra.
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader className="animate-spin text-amber-500" size={24} />
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <CloudDownload size={36} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium text-gray-500">Aucun document en attente</p>
              <p className="text-xs mt-1">Les documents envoyés via N8N (Google Drive) apparaîtront ici.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map(doc => (
                <PendingRow
                  key={doc.id}
                  doc={doc}
                  onApprove={fetchPending}
                  onReject={fetchPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info flux */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-gray-600">Flux Google Drive → Lyra</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="text-xs text-gray-600 space-y-2 leading-relaxed list-none">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">1</span>
              <span><strong>N8N</strong> surveille un dossier Google Drive et envoie automatiquement les nouveaux fichiers à Lyra via le endpoint sécurisé <code className="bg-gray-100 px-1 rounded">/api/lyra-agent/documents/ingest</code></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">2</span>
              <span><strong>Validation admin</strong> — Les documents apparaissent ici avec statut "En attente". Prévisualisez le fichier avant d'approuver.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">3</span>
              <span><strong>Indexation</strong> — Approuver lance l'extraction de texte et l'indexation dans la base RAG de Lyra. Rejeter supprime le fichier.</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default LyraPendingDocuments;
